import { AnyAction, ThunkDispatch, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import ReactGridLayout from 'react-grid-layout';
import { StateInterface } from 'Interfaces/StateInterface';
import { DashboardDataPointInterface } from 'Interfaces/DashBoardInterface';
import { getLocalization } from 'global/global';
import { cleanLayouts, getLayout } from 'views/Dashboard/util';
import {
  dashboardsLoaded, dashboardUpdated, setDashboardShowOnLogin, updateDashboardLayout
} from '../reducers/dashboard';
import { request } from '../api/request';
import { getRequestConfig } from '../api';
import { DASHBOARD_ACTIONS } from './actionTypes';

export const selectDashboard = (active?: string) => ({
  type: DASHBOARD_ACTIONS.SELECT, active
});

export const saveNewDashboard = createAsyncThunk(
  'dashboard/new',
  async (name: string, thunkApi) => {
    const state = thunkApi.getState() as StateInterface;
    const config = getRequestConfig();
    const data = await request(
      `/json/chart/dashboard`,
      {...config, method: 'POST', body: JSON.stringify({ name })},
      thunkApi.dispatch as ThunkDispatch<StateInterface, any, AnyAction>,
      () => state
    ).then(res => res.json()).catch(e => {
      console.log(e);
    });
    if (data.id) {
      return data;
    }
    return null;
  }
);

export const getDashboards = createAsyncThunk(
  'dashboards/get',
  async (arge, thunkApi) => {
    const state = thunkApi.getState() as StateInterface;
    const config = getRequestConfig();
    const data = await request(
      `/json/chart/dashboards`,
      {...config},
      thunkApi.dispatch as ThunkDispatch<StateInterface, any, AnyAction>,
      () => state
    ).then(res => res.json()).catch(e => {
      console.log(e);
    });
    if (Array.isArray(data)) {
      const userId = Number(state.clientPersist.user_id);
      const selected = data.find(d => d.showOnLogin && d.userId === userId);
      if (selected) {
        thunkApi.dispatch(selectDashboard(`${selected.id}`));
      }
      console.log('layout cleanup');
      console.log(data);
      cleanLayouts(data);
      console.log(data);
      thunkApi.dispatch(dashboardsLoaded(data));
      return data;
    }
    return null;
  }
);


export const addChartToDashboard = createAsyncThunk(
  'dashboard/addChart',
  async (args: { dashboardId: number; chartId: number}, thunkApi) => {
    const state = thunkApi.getState() as StateInterface;
    const config = getRequestConfig();
    const data = await request(
      `/json/chart/dashboard/${args.dashboardId}/${args.chartId}`,
      {...config, method: 'POST'},
      thunkApi.dispatch as ThunkDispatch<StateInterface, any, AnyAction>,
      () => state
    ).then(res => res.json()).catch(e => {
      console.log(e);
    });
    if (data.id) {
      const dashboard = state.dashboard.collection.find(d => d.id === args.dashboardId);
      thunkApi.dispatch(dashboardUpdated(data));
      if (dashboard) {
        const layout = dashboard.layout || [];
        const length = layout.length;
        const column = dashboard.columns || 2;
        const newLayout = layout.concat([getLayout(`${args.chartId}`, length, column)]);
        void thunkApi.dispatch(updateLayout({ dashboardId: dashboard.id, layout: newLayout, columns: column }));
      }
      return data;
    }
    return null;
  }
);

export const removeChartFromDashboard = createAsyncThunk(
  'dashboard/removeChart',
  async (args: { dashboardId: number; chartId: number}, thunkApi) => {
    const state = thunkApi.getState() as StateInterface;
    const config = getRequestConfig();
    const data = await request(
      `/json/chart/dashboard/${args.dashboardId}/${args.chartId}`,
      {...config, method: 'GET'},
      thunkApi.dispatch as ThunkDispatch<StateInterface, any, AnyAction>,
      () => state
    ).then(res => res.json()).catch(e => {
      console.log(e);
    });
    if (data.id) {
      const dashboard = state.dashboard.collection.find(d => d.id === args.dashboardId);
      thunkApi.dispatch(dashboardUpdated(data));
      if (dashboard) {
        const layout = dashboard.layout || [];
        let newLayout = layout.filter(l => l.i !== `${args.chartId}`);
        const column = dashboard.columns || 2;
        newLayout = newLayout.map((l, i) => {
          return getLayout(`${l.i}`, i, column);
        });
        void thunkApi.dispatch(updateLayout({
          dashboardId: dashboard.id, layout: newLayout, columns: column
        }));
      }
      return data;
    }
    return null;
  }
);

export const deleteDashboard = createAsyncThunk(
  'dashboard/deleteDashboard',
  async (args: { dashboardId: number }, thunkApi) => {
    const state = thunkApi.getState() as StateInterface;
    const config = getRequestConfig();
    const toastId = Date.now();
    toast(getLocalization('deletingDashboard'), {
      toastId: toastId,
      type: toast.TYPE.INFO,
      autoClose: false,
      closeButton: false,
      hideProgressBar: true,
      closeOnClick: false,
    });
    const data = await request(
      `/json/chart/dashboard/${args.dashboardId}`,
      {...config, method: 'DELETE'},
      thunkApi.dispatch as ThunkDispatch<StateInterface, any, AnyAction>,
      () => state
    ).then(res => res.json()).catch(e => {
      console.log(e);
    });
    if (data.status === 'OK') {
      toast.update(toastId, {
        type: toast.TYPE.SUCCESS,
        render: getLocalization('dashboardDeleted'),
      });
      thunkApi.dispatch(selectDashboard(undefined));
    } else {
      toast.update(toastId, {
        type: toast.TYPE.ERROR,
        render: getLocalization('dashboardDeletedError'),
      });
    }
    setTimeout(() => toast.dismiss(toastId), 3000);
    return {...data, dashboardId: args.dashboardId};
  }
);

export const updateLayout = createAsyncThunk(
  'dashboard/updateLayout',
  async (args: { dashboardId: number; layout: ReactGridLayout.Layout[]; columns: number }, thunkApi) => {
    const state = thunkApi.getState() as StateInterface;
    const config = getRequestConfig();
    await request(
      `/json/chart/dashboard/layout/${args.dashboardId}`,
      {...config, method: 'POST', body: JSON.stringify({ layout: args.layout, columns: args.columns })},
      thunkApi.dispatch as ThunkDispatch<StateInterface, any, AnyAction>,
      () => state
    ).then(res => res.json()).then((json) => {
      if (json.status === 'OK') {
        thunkApi.dispatch(updateDashboardLayout({...args}));
      }
    }).catch(e => {
      console.log(e);
    });
    return null;
  }
);

export const setShowOnLogin = createAsyncThunk(
  'dashboard/setShowOnLogin',
  async (args: { dashboardId: number; showOnLogin: boolean}, thunkApi) => {
    const state = thunkApi.getState() as StateInterface;
    const config = getRequestConfig();
    const { dashboardId, showOnLogin } = args;
    await request(
      `/json/chart/dashboard/setShowOnLogin`,
      {...config, method: 'POST', body: JSON.stringify({ showOnLogin, id: dashboardId })},
      thunkApi.dispatch as ThunkDispatch<StateInterface, any, AnyAction>,
      () => state
    ).then(res => res.json()).then(json => {
      if (json.status === 'OK') {
        thunkApi.dispatch(setDashboardShowOnLogin({...args}));
      }
    }).catch(e => {
      console.log(e);
    });
    return null;
  }
);

export const addDataPointsToDashboard = createAsyncThunk(
  'dashboard/addDataPoints',
  async (args: { dashboardId: number; dataPoints: DashboardDataPointInterface[]}, thunkApi) => {
    const state = thunkApi.getState() as StateInterface;
    const config = getRequestConfig();
    const data = await request(
      `/json/chart/dashboard/addDataPoints/${args.dashboardId}`,
      {...config, method: 'PUT', body: JSON.stringify(args.dataPoints)},
      thunkApi.dispatch as ThunkDispatch<StateInterface, any, AnyAction>,
      () => state
    ).then(res => res.json()).catch(e => {
      console.log(e);
    });
    if (data.id) {
      const dashboard = state.dashboard.collection.find(d => d.id === args.dashboardId);
      thunkApi.dispatch(dashboardUpdated(data));
      if (dashboard) {
        const layout = dashboard.layout || [];
        const length = layout.length;
        const column = dashboard.columns || 2;
        const newLayout = layout.concat(args.dataPoints.map(dp => (getLayout(`${dp.id}`, length, column))));
        void thunkApi.dispatch(updateLayout({ dashboardId: dashboard.id, layout: newLayout, columns: column }));
      }
      return data;
    }
    return null;
  }
);

export const removeDataPointFromDashboard = createAsyncThunk(
  'dashboard/removeDataPoint',
  async (args: { dashboardId: number; dataPointId: string}, thunkApi) => {
    const state = thunkApi.getState() as StateInterface;
    const config = getRequestConfig();
    const data = await request(
      `/json/chart/dashboard/data-point/${args.dashboardId}/${args.dataPointId}`,
      {...config, method: 'DELETE'},
      thunkApi.dispatch as ThunkDispatch<StateInterface, any, AnyAction>,
      () => state
    ).then(res => res.json()).catch(e => {
      console.log(e);
    });
    if (data.id) {
      const dashboard = state.dashboard.collection.find(d => d.id === args.dashboardId);
      thunkApi.dispatch(dashboardUpdated(data));
      if (dashboard) {
        const layout = dashboard.layout || [];
        const column = dashboard.columns || 2;
        const newLayout = layout.filter(l => l.i !== `${args.dataPointId}`).map((l, i) => {
          return getLayout(`${l.i}`, i, column);
        });
        void thunkApi.dispatch(updateLayout({ dashboardId: dashboard.id, layout: newLayout, columns: column }));
      }
      return data;
    }
    return null;
  }
);
